import { ArrowLeftRight, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./organization-card.i18n";

type OrganizationCardProps = {
  name: string | undefined;
  // True when the organization was restored from a previous visit rather than
  // typed in — shows the "Last used organization" hint.
  isRestored?: boolean;
  onChange: () => void;
};

/**
 * The resolved-organization banner shown above the pre-login forms (sign-in,
 * forgot password) with an icon-only "Change organization" button that expands
 * on hover/focus.
 */
export function OrganizationCard({ name, isRestored = false, onChange }: OrganizationCardProps) {
  const t = useT(T);

  return (
    <div className="relative mb-4 p-3 bg-muted rounded-md">
      <div className="flex items-center gap-3 min-w-0 pr-10">
        <div
          className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 font-semibold text-primary"
          aria-hidden="true"
        >
          {name ? name.charAt(0).toUpperCase() : <Building2 className="size-4" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate" title={name}>
            {name}
          </p>
          {isRestored && (
            <p className="text-xs text-muted-foreground truncate">{t.lastUsedOrganization}</p>
          )}
        </div>
      </div>
      {/* Icon-only button that expands over the tenant name on hover/focus */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onChange}
        tooltip={t.changeOrganization}
        className="group absolute right-2 top-1/2 -translate-y-1/2 px-[7px] gap-0 cursor-pointer hover:bg-card hover:text-foreground hover:shadow-md focus-visible:bg-card"
      >
        <ArrowLeftRight aria-hidden="true" />
        <span className="grid grid-cols-[0fr] transition-[grid-template-columns] duration-300 ease-out group-hover:grid-cols-[1fr] group-focus-visible:grid-cols-[1fr]">
          <span className="overflow-hidden whitespace-nowrap pl-0 opacity-0 transition-all duration-300 ease-out group-hover:pl-2 group-hover:opacity-100 group-focus-visible:pl-2 group-focus-visible:opacity-100">
            {t.change}
          </span>
        </span>
      </Button>
    </div>
  );
}
